/**
 * Período do mês do app (ADR 0190): o "mês" que a Home, as Transações, a Análise e os
 * relatórios mostram pode começar em outro dia que não o 1º — quem recebe dia 20 vive de
 * 20 a 19. Módulo PURO: só chaves `yyyy-MM`, datas `yyyy-MM-dd` e o Dia de início (1–31).
 *
 * A matemática é a do Ciclo do orçamento (ADR 0120, `lib/budgetCycle.ts`), reaproveitada de
 * propósito: ciclos que se encostam, dia 31 em mês curto vira o último dia, e **a chave do
 * período é a regra da maioria dos dias** — Dia de início até 15, o período pertence ao mês
 * em que começa; 16 em diante, ao mês em que termina. Assim a Home e um Orçamento de mesmo
 * dia ficam na mesma janela sem mexer no ADR 0120. A chave é identidade interna (query
 * keys, deep links, score mensal); o usuário vê o intervalo (`lib/monthCycleFormat.ts`).
 *
 * Quem precisa do dia efetivo (preferência + gate Pro) lê `lib/monthStart.ts`; aqui o dia
 * é sempre parâmetro, pra ser testável e servir ao Web com a própria fonte.
 */
import { addDays, addMonths, differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
    CYCLE_END_MONTH_THRESHOLD_DAY,
    cycleContaining,
    cycleMonth,
    cyclesOfMonth,
    type BudgetCycle,
    type BudgetCycleSpec,
} from '@/lib/budgetCycle';

/** Dia 1 = mês-calendário, o padrão e o comportamento histórico do app. */
export const CALENDAR_MONTH_START_DAY = 1;
export const MIN_MONTH_START_DAY = 1;
export const MAX_MONTH_START_DAY = 31;

/** Janela inclusiva [startDate, endDate], ambas `yyyy-MM-dd`. */
export type MonthCycle = BudgetCycle;

const ISO_DATE = 'yyyy-MM-dd';
const ISO_MONTH = 'yyyy-MM';

/** Qualquer coisa fora de 1–31 (ou não numérica) cai no mês-calendário. */
export function normalizeMonthStartDay(value: unknown): number {
    const raw = Math.trunc(Number(value));
    if (!Number.isFinite(raw)) return CALENDAR_MONTH_START_DAY;
    return Math.min(Math.max(raw, MIN_MONTH_START_DAY), MAX_MONTH_START_DAY);
}

export function isCalendarMonthStartDay(startDay: number): boolean {
    return normalizeMonthStartDay(startDay) === CALENDAR_MONTH_START_DAY;
}

function specOf(startDay: number): BudgetCycleSpec {
    return { period: 'monthly', cycleStartDay: normalizeMonthStartDay(startDay), cycleAnchorDate: null };
}

/** O período que a chave `yyyy-MM` nomeia, para o Dia de início dado. */
export function monthCycleForKey(monthKey: string, startDay: number): MonthCycle {
    return cyclesOfMonth(specOf(startDay), monthKey)[0];
}

/** A chave (`yyyy-MM`) do período que contém a data. */
export function monthKeyForDate(date: string, startDay: number): string {
    const spec = specOf(startDay);
    return cycleMonth(spec, cycleContaining(spec, date));
}

/** O período que contém a data. */
export function monthCycleContaining(date: string, startDay: number): MonthCycle {
    return cycleContaining(specOf(startDay), date);
}

/** A chave do período de hoje. */
export function todayMonthKey(startDay: number, today: Date = new Date()): string {
    return monthKeyForDate(format(today, ISO_DATE), startDay);
}

/** Chave N meses à frente (ou atrás). A chave é mês-calendário, então a aritmética também é. */
export function shiftMonthKey(monthKey: string, delta: number): string {
    return format(addMonths(parseISO(`${monthKey}-01`), delta), ISO_MONTH);
}

/** Todas as chaves de `startKey` a `endKey`, inclusive, em ordem. */
export function monthKeysBetween(startKey: string, endKey: string): string[] {
    const keys: string[] = [];
    for (let key = startKey; key <= endKey; key = shiftMonthKey(key, 1)) keys.push(key);
    return keys;
}

/** As chaves vizinhas de `monthKey`: `before` pra trás e `after` pra frente, em ordem, com ela no meio. */
export function monthKeysAround(monthKey: string, before: number, after: number): string[] {
    return monthKeysBetween(shiftMonthKey(monthKey, -before), shiftMonthKey(monthKey, after));
}

/** Janela que cobre do período de `startKey` ao de `endKey` (ambos inclusive). */
export function monthKeyRange(startKey: string, endKey: string, startDay: number): MonthCycle {
    return {
        startDate: monthCycleForKey(startKey, startDay).startDate,
        endDate: monthCycleForKey(endKey, startDay).endDate,
    };
}

export function isDateInMonthCycle(cycle: MonthCycle, date: string): boolean {
    return date >= cycle.startDate && date <= cycle.endDate;
}

/** Quantos dias o período tem. */
export function monthCycleLength(cycle: MonthCycle): number {
    return differenceInCalendarDays(parseISO(cycle.endDate), parseISO(cycle.startDate)) + 1;
}

/**
 * Posição do dia dentro do período, de 1 ao tamanho do ciclo. É o "dia N" dos gráficos de
 * ritmo: com Dia de início 20, o 20/out é o dia 1 e o 19/nov o dia 31. Fora do período,
 * devolve um valor fora de [1, tamanho] — quem chama decide o recorte.
 */
export function dayIndexInMonthCycle(cycle: MonthCycle, date: string): number {
    return differenceInCalendarDays(parseISO(date), parseISO(cycle.startDate)) + 1;
}

/** A data do dia de índice `index` (1-based) do período. */
export function dateAtMonthCycleIndex(cycle: MonthCycle, index: number): string {
    return format(addDays(parseISO(cycle.startDate), index - 1), ISO_DATE);
}

/** Os dias do período, em ordem. */
export function monthCycleDays(cycle: MonthCycle): string[] {
    const length = monthCycleLength(cycle);
    const days: string[] = [];
    for (let index = 1; index <= length; index += 1) days.push(dateAtMonthCycleIndex(cycle, index));
    return days;
}

/**
 * A MESMA chave, em SQL (SQLite), para uma coluna/expressão `yyyy-MM-dd`. É o que as
 * consultas que agrupam por mês usam no lugar de `substr(date, 1, 7)`. Espelha
 * `monthKeyForDate` passo a passo — o teste de propriedade em
 * `lib/__tests__/monthCycle.test.ts` roda as duas sobre todos os dias de quatro anos e
 * todos os Dias de início, e é o portão de quem mexer em qualquer um dos lados.
 *
 * Como lê: `startsCycle` diz se o dia começa um período no próprio mês (dia >= Dia de
 * início, encurtado ao tamanho do mês, igual a `monthlyCycleStartingIn`). Dia de início
 * até 15: a chave é o mês em que o período começa (este mês se começa aqui, senão o
 * anterior). 16 em diante: o mês em que termina (o próximo se começa aqui, senão este).
 */
export function monthKeySqlText(column: string, startDay: number): string {
    const day = normalizeMonthStartDay(startDay);
    if (day === CALENDAR_MONTH_START_DAY) return `substr(${column}, 1, 7)`;
    const dayOfMonth = `CAST(strftime('%d', ${column}) AS INTEGER)`;
    const daysInMonth = `CAST(strftime('%d', date(${column}, 'start of month', '+1 month', '-1 day')) AS INTEGER)`;
    const startsCycle = `${dayOfMonth} >= MIN(${day}, ${daysInMonth})`;
    const thisMonth = `strftime('%Y-%m', ${column})`;
    if (day < CYCLE_END_MONTH_THRESHOLD_DAY) {
        const previousMonth = `strftime('%Y-%m', date(${column}, 'start of month', '-1 month'))`;
        return `(CASE WHEN ${startsCycle} THEN ${thisMonth} ELSE ${previousMonth} END)`;
    }
    const nextMonth = `strftime('%Y-%m', date(${column}, 'start of month', '+1 month'))`;
    return `(CASE WHEN ${startsCycle} THEN ${nextMonth} ELSE ${thisMonth} END)`;
}
